import { useEffect, useMemo, useState } from 'react';
import {
  ANALYTICS_GROUPS,
  ANALYTICS_GROUP_LABEL,
  ANALYTICS_SCOPE_LABEL,
  EXPORT_KIND_LABEL,
  ORDER_STATUSES,
  ORDER_STATUS_LABEL,
  type AnalyticsGroupBy,
  type AnalyticsMeta,
  type AnalyticsQuery,
  type AnalyticsReport,
  type AnalyticsScope,
  type ExportKind,
  type LeaderBoard,
  type OrderStatus,
} from 'shared';
import { api, type DealRow } from '../api';
import Icon from '../Icon';
import { alert, haptic } from '../telegram';

const money = (v: number) => `${Math.round(v).toLocaleString('ru-RU')} ₽`;

/** Крупные суммы в плитках: 1 245 300 ₽ не читается с телефона. */
function short(v: number): string {
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)} млн ₽`;
  if (abs >= 100_000) return `${Math.round(v / 1000)} тыс. ₽`;
  return money(v);
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** Готовые периоды. Руками даты вводят редко, но возможность нужна. */
const PRESETS = [
  { key: 'week', label: 'Неделя', days: 7 },
  { key: 'month', label: 'Месяц', days: 30 },
  { key: 'quarter', label: 'Квартал', days: 90 },
  { key: 'year', label: 'Год', days: 365 },
] as const;

/**
 * Столбики по строкам отчёта.
 *
 * Не диаграмма, а полоска прямо в таблице: взгляд и так идёт по строкам,
 * и отдельный график заставлял бы сопоставлять его с ними глазами.
 */
function Bar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <span className="rbar" aria-hidden="true">
      <span className="rbar__fill" style={{ width: `${pct}%` }} />
    </span>
  );
}

interface Props {
  isAdmin: boolean;
}

/**
 * Отчёты и выгрузки.
 *
 * Один экран на три роли: администратор видит площадку, владелец — свою
 * компанию, логист — свои заявки. Разделять на три страницы смысла нет:
 * вопросы у всех одни и те же, отличается только круг сделок, и он
 * задаётся сервером, а не тем, что показано в интерфейсе.
 */
export default function Analytics({ isAdmin }: Props) {
  const [meta, setMeta] = useState<AnalyticsMeta | null>(null);
  const [report, setReport] = useState<AnalyticsReport | null>(null);
  const [deals, setDeals] = useState<DealRow[] | null>(null);
  const [leaders, setLeaders] = useState<LeaderBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState<'report' | 'deals' | 'leaders'>('report');
  const [openFilters, setOpenFilters] = useState(false);

  const [scope, setScope] = useState<AnalyticsScope>(isAdmin ? 'platform' : 'mine');
  const [groupBy, setGroupBy] = useState<AnalyticsGroupBy>('day');
  const [preset, setPreset] = useState<string>('quarter');
  const [from, setFrom] = useState(iso(new Date(Date.now() - 90 * 864e5)));
  const [to, setTo] = useState(iso(new Date()));
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [driverId, setDriverId] = useState('');
  const [logistId, setLogistId] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [vehicleId, setVehicleId] = useState('');

  const query: AnalyticsQuery = useMemo(
    () => ({
      scope,
      groupBy,
      from,
      // Верхняя граница — конец дня: иначе сделки сегодняшнего дня
      // не попадают в отчёт, и человек считает, что данных нет.
      to: `${to}T23:59:59`,
      status: status || undefined,
      driverId: driverId || undefined,
      logistId: logistId || undefined,
      companyId: companyId || undefined,
      vehicleId: vehicleId || undefined,
    }),
    [scope, groupBy, from, to, status, driverId, logistId, companyId, vehicleId],
  );

  useEffect(() => {
    api
      .analyticsMeta({ scope })
      .then((m) => {
        setMeta(m);
        // Область могла оказаться недоступной — сервер вернёт свой список,
        // и держать в состоянии то, чего нет, нельзя.
        if (!m.scopes.includes(scope)) setScope(m.scopes[m.scopes.length - 1]);
      })
      .catch(() => {});
  }, [scope]);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    const load = async () => {
      try {
        if (tab === 'leaders') {
          const r = await api.analyticsLeaders({ from, to: `${to}T23:59:59` });
          if (alive) setLeaders(r);
        } else if (tab === 'deals') {
          const r = await api.analyticsDeals(query);
          if (alive) setDeals(r.items);
        } else {
          const r = await api.analyticsReport(query);
          if (alive) setReport(r);
        }
      } catch {
        // Пустой экран честнее сообщения об ошибке: чаще всего это
        // просто отсутствие сделок в выбранном периоде.
      } finally {
        if (alive) setLoading(false);
      }
    };

    void load();
    return () => {
      alive = false;
    };
  }, [tab, query, from, to]);

  function applyPreset(key: string, days: number) {
    haptic();
    setPreset(key);
    setFrom(iso(new Date(Date.now() - days * 864e5)));
    setTo(iso(new Date()));
  }

  async function exportAs(kind: ExportKind) {
    haptic();
    setBusy(true);
    try {
      await api.exportCsv(kind, query);
    } catch {
      await alert('Не удалось выгрузить. Попробуйте сузить период.');
    } finally {
      setBusy(false);
    }
  }

  const filtersOn = [status, driverId, logistId, companyId, vehicleId].filter(
    Boolean,
  ).length;

  const t = report?.totals;
  const maxRevenue = Math.max(1, ...(report?.rows ?? []).map((r) => r.revenueRub));

  return (
    <>
      <h2>Аналитика</h2>

      {/* Область — первое, что нужно выбрать: от неё зависят все цифры ниже.
          Если доступна только одна, переключатель не показываем. */}
      {meta && meta.scopes.length > 1 && (
        <div className="seg" role="tablist" aria-label="Область отчёта">
          {meta.scopes.map((s) => (
            <button
              key={s}
              role="tab"
              aria-selected={scope === s}
              className={scope === s ? 'seg__btn seg__btn--on' : 'seg__btn'}
              onClick={() => {
                haptic();
                setScope(s);
              }}
            >
              {ANALYTICS_SCOPE_LABEL[s]}
            </button>
          ))}
        </div>
      )}

      <div className="chips" style={{ marginTop: 'var(--s3)' }}>
        {PRESETS.map((p) => (
          <button
            key={p.key}
            className={preset === p.key ? 'chip chip--on' : 'chip'}
            onClick={() => applyPreset(p.key, p.days)}
          >
            {p.label}
          </button>
        ))}
        <button
          className={
            openFilters || filtersOn > 0 ? 'chip chip--applied' : 'chip chip--ghost'
          }
          onClick={() => {
            haptic();
            setOpenFilters((v) => !v);
          }}
        >
          <Icon name="filter" size={14} />
          Фильтры{filtersOn > 0 ? ` · ${filtersOn}` : ''}
        </button>
      </div>

      {openFilters && meta && (
        <div className="card card--inset" style={{ marginTop: 'var(--s3)' }}>
          <div className="field-row">
            <div className="field">
              <label>С даты</label>
              <input
                type="date"
                value={from}
                max={to}
                onChange={(e) => {
                  setPreset('');
                  setFrom(e.target.value);
                }}
              />
            </div>
            <div className="field">
              <label>По дату</label>
              <input
                type="date"
                value={to}
                min={from}
                onChange={(e) => {
                  setPreset('');
                  setTo(e.target.value);
                }}
              />
            </div>
          </div>

          <div className="field">
            <label>Статус рейса</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as OrderStatus | '')}
            >
              <option value="">Любой</option>
              {ORDER_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ORDER_STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Исполнитель</label>
            <select value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Все</option>
              {meta.drivers.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>

          <div className="field">
            <label>Логист</label>
            <select value={logistId} onChange={(e) => setLogistId(e.target.value)}>
              <option value="">Все</option>
              {meta.logists.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                </option>
              ))}
            </select>
          </div>

          {meta.vehicles.length > 0 && (
            <div className="field">
              <label>Машина</label>
              <select value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
                <option value="">Все</option>
                {meta.vehicles.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.plate}
                  </option>
                ))}
              </select>
            </div>
          )}

          {meta.companies.length > 0 && (
            <div className="field">
              <label>Компания</label>
              <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
                <option value="">Все</option>
                {meta.companies.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {filtersOn > 0 && (
            <button
              className="ghost"
              onClick={() => {
                haptic();
                setStatus('');
                setDriverId('');
                setLogistId('');
                setCompanyId('');
                setVehicleId('');
              }}
            >
              Сбросить фильтры
            </button>
          )}
        </div>
      )}

      {/* Плитки с итогом. Подписи развёрнутые: «Маржа» без пояснения
          каждый читает по-своему, а цифра в отчёте должна быть однозначной. */}
      {t && (
        <div className="kpis">
          <div className="kpi" title="Сколько рейсов попало в выбранный период">
            <span className="kpi__label">Рейсов</span>
            <strong className="kpi__value">{t.orders}</strong>
            <span className="meta">завершено {t.done}</span>
          </div>
          <div className="kpi" title="Сумма, которую заплатили заказчики">
            <span className="kpi__label">Оборот</span>
            <strong className="kpi__value">{short(t.revenueRub)}</strong>
            <span className="meta">с наценкой</span>
          </div>
          <div className="kpi" title="Выплаты исполнителям за рейсы">
            <span className="kpi__label">Исполнителям</span>
            <strong className="kpi__value">{short(t.payoutRub)}</strong>
            <span className="meta">на руки</span>
          </div>
          <div className="kpi" title="Оборот минус выплаты исполнителям">
            <span className="kpi__label">Маржа</span>
            <strong className="kpi__value">{short(t.marginRub)}</strong>
            <span className="meta">
              {t.revenueRub > 0
                ? `${Math.round((t.marginRub / t.revenueRub) * 100)}% от оборота`
                : '—'}
            </span>
          </div>
        </div>
      )}

      <div className="seg" style={{ marginTop: 'var(--s3)' }}>
        <button
          className={tab === 'report' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('report')}
        >
          Сводка
        </button>
        <button
          className={tab === 'deals' ? 'seg__btn seg__btn--on' : 'seg__btn'}
          onClick={() => setTab('deals')}
        >
          Сделки
        </button>
        {isAdmin && (
          <button
            className={tab === 'leaders' ? 'seg__btn seg__btn--on' : 'seg__btn'}
            onClick={() => setTab('leaders')}
          >
            Рейтинг
          </button>
        )}
      </div>

      {tab === 'report' && (
        <div className="chips" style={{ marginTop: 'var(--s3)' }}>
          {ANALYTICS_GROUPS.map((g) => (
            <button
              key={g}
              className={groupBy === g ? 'chip chip--on' : 'chip'}
              onClick={() => {
                haptic();
                setGroupBy(g);
              }}
            >
              {ANALYTICS_GROUP_LABEL[g]}
            </button>
          ))}
        </div>
      )}

      {loading && <div className="meta" style={{ padding: 'var(--s4)' }}>Считаю…</div>}

      {!loading && tab === 'report' && report && (
        report.rows.length === 0 ? (
          <div className="empty">
            За выбранный период сделок нет.
            <br />
            Расширьте период или снимите фильтры.
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{ANALYTICS_GROUP_LABEL[report.groupBy].replace('По ', '')}</th>
                  <th className="num">Рейсов</th>
                  <th className="num">Завершено</th>
                  <th className="num">Оборот</th>
                  <th className="num">Исполнителям</th>
                  <th className="num">Маржа</th>
                  <th className="num">Км</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.key}>
                    <td>
                      <span className="tbl__name">{r.label}</span>
                      <Bar value={r.revenueRub} max={maxRevenue} />
                    </td>
                    <td className="num" data-label="Рейсов">{r.orders}</td>
                    <td className="num" data-label="Завершено">{r.done}</td>
                    <td className="num" data-label="Оборот">{money(r.revenueRub)}</td>
                    <td className="num" data-label="Исполнителям">
                      {money(r.payoutRub)}
                    </td>
                    <td className="num" data-label="Маржа">{money(r.marginRub)}</td>
                    <td className="num" data-label="Км">
                      {r.distanceKm.toLocaleString('ru-RU')}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Итого</td>
                  <td className="num" data-label="Рейсов">{report.totals.orders}</td>
                  <td className="num" data-label="Завершено">{report.totals.done}</td>
                  <td className="num" data-label="Оборот">
                    {money(report.totals.revenueRub)}
                  </td>
                  <td className="num" data-label="Исполнителям">
                    {money(report.totals.payoutRub)}
                  </td>
                  <td className="num" data-label="Маржа">
                    {money(report.totals.marginRub)}
                  </td>
                  <td className="num" data-label="Км">
                    {report.totals.distanceKm.toLocaleString('ru-RU')}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'deals' && deals && (
        deals.length === 0 ? (
          <div className="empty">Сделок за период нет.</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Заявка</th>
                  <th>Маршрут</th>
                  <th>Статус</th>
                  <th>Исполнитель</th>
                  <th className="num">Заказчик</th>
                  <th className="num">Исполнителю</th>
                  <th className="num">Расходы</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((x) => (
                  <tr key={x.orderId}>
                    <td>
                      <span className="tbl__name">№ {x.number}</span>
                      <span className="meta">
                        {new Date(x.createdAt).toLocaleDateString('ru-RU')}
                      </span>
                    </td>
                    <td data-label="Маршрут">
                      <span className="tbl__name">
                        {x.fromRegion} → {x.toRegion}
                      </span>
                      <span className="meta">{x.distanceKm} км</span>
                    </td>
                    <td data-label="Статус">
                      {ORDER_STATUS_LABEL[x.status as OrderStatus] ?? x.status}
                    </td>
                    <td data-label="Исполнитель">
                      <span className="tbl__name">{x.driverName}</span>
                      {x.vehiclePlate && <span className="meta">{x.vehiclePlate}</span>}
                    </td>
                    <td className="num" data-label="Заказчик">
                      {money(x.shipperTotalRub)}
                    </td>
                    <td className="num" data-label="Исполнителю">
                      {money(x.driverPayoutRub)}
                    </td>
                    <td className="num" data-label="Расходы">
                      {money(x.carrierExpenses + x.shipperExpenses)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {!loading && tab === 'leaders' && leaders && (
        <>
          <h3 className="sect">Логисты по публикациям</h3>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Логист</th>
                  <th className="num">Заявок</th>
                  <th className="num">Рейсов</th>
                  <th className="num">Доехало</th>
                  <th className="num">Оборот</th>
                  <th className="num">Успешных</th>
                </tr>
              </thead>
              <tbody>
                {leaders.logists.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <span className="tbl__name">{r.name}</span>
                      {r.login && <span className="meta">{r.login}</span>}
                    </td>
                    <td className="num" data-label="Заявок">{r.published}</td>
                    <td className="num" data-label="Рейсов">{r.taken}</td>
                    <td className="num" data-label="Доехало">{r.done}</td>
                    <td className="num" data-label="Оборот">{short(r.revenueRub)}</td>
                    <td className="num" data-label="Успешных">
                      {r.successRate === null ? '—' : `${r.successRate}%`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="sect">Исполнители по доставкам</h3>
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Исполнитель</th>
                  <th className="num">Взято</th>
                  <th className="num">Доставлено</th>
                  <th className="num">Отменено</th>
                  <th className="num">Км</th>
                  <th className="num">Заработал</th>
                  <th className="num">Рейтинг</th>
                </tr>
              </thead>
              <tbody>
                {leaders.carriers.map((r) => (
                  <tr key={r.userId}>
                    <td>
                      <span className="tbl__name">{r.name}</span>
                      {r.login && <span className="meta">{r.login}</span>}
                    </td>
                    <td className="num" data-label="Взято">{r.taken}</td>
                    <td className="num" data-label="Доставлено">{r.done}</td>
                    <td className="num" data-label="Отменено">{r.cancelled}</td>
                    <td className="num" data-label="Км">
                      {r.distanceKm.toLocaleString('ru-RU')}
                    </td>
                    <td className="num" data-label="Заработал">
                      {short(r.payoutRub)}
                    </td>
                    <td className="num" data-label="Рейтинг">{r.ratingAvg ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Выгрузка внизу: сначала человек настраивает выборку и видит её
          на экране, и только потом уносит — те же фильтры, те же цифры. */}
      {meta && (
        <>
          <h3 className="sect">Выгрузка в CSV</h3>
          <div className="chips">
            {meta.exports.map((k) => (
              <button
                key={k}
                className="chip"
                disabled={busy}
                onClick={() => void exportAs(k)}
              >
                <Icon name="download" size={14} />
                {EXPORT_KIND_LABEL[k]}
              </button>
            ))}
          </div>
          <p className="meta" style={{ marginTop: 'var(--s2)' }}>
            Файл откроется в Excel и Google Таблицах. Сделки, заявки и расходы
            выгружаются за выбранный период и с учётом фильтров.
          </p>
        </>
      )}
    </>
  );
}
